import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Download, FileSpreadsheet, Loader2, Sparkles, Upload, X } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { t } from '@/locales/i18n';
import { detectHeaderRow } from '@/lib/import-templates';

export interface ImportField {
  /** Cible (ex. raison_sociale, email, téléphone) */
  key: string;
  label: string;
  required?: boolean;
  /** Optional pre-process before passing to onImport */
  transform?: (raw: string) => string | number | null;
}

interface ImportDialogProps<T> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fields: ImportField[];
  /** Callback once mapping is ready and import is launched */
  onImport: (rows: T[]) => Promise<{ success: number; errors: string[] }>;
  title?: string;
  /** Generate XLSX template (returns Blob) — wires the download button. */
  generateTemplate?: () => Promise<Blob> | Blob;
  templateFilename?: string;
  /**
   * Sprint 6.9.1.1 — colonnes signature pour auto-détecter la ligne d'en-tête réelle.
   * Indispensable pour les fichiers KOST avec ligne 1 décorative (titre fusionné).
   * Si fourni : parser fait un pré-scan sur les 10 premières lignes en mode raw, identifie la
   * première ligne contenant ≥ 50 % de ces headers, slice à partir de là.
   * Si omis : comportement legacy (ligne 1 = headers, fallback SheetJS).
   */
  requiredHeaders?: string[];
}

export function ImportDialog<T>({
  open,
  onOpenChange,
  fields,
  onImport,
  title,
  generateTemplate,
  templateFilename,
  requiredHeaders,
}: ImportDialogProps<T>): JSX.Element {
  const [fileName, setFileName] = useState<string | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<{ success: number; errors: string[] } | null>(null);
  // Sprint 6.9.1.1 — index 0-based de la ligne d'en-tête détectée dans le fichier source.
  const [detectedHeaderRow, setDetectedHeaderRow] = useState<number | null>(null);

  const reset = (): void => {
    setFileName(null);
    setHeaders([]);
    setRows([]);
    setMapping({});
    setResult(null);
    setDetectedHeaderRow(null);
  };

  const downloadTemplate = async (): Promise<void> => {
    if (!generateTemplate) return;
    const blob = await generateTemplate();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = templateFilename ?? 'template.xlsx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const onFile = async (file: File): Promise<void> => {
    setResult(null);
    setDetectedHeaderRow(null);
    const xlsx = await import('xlsx');
    const buffer = await file.arrayBuffer();
    const wb = xlsx.read(buffer, { type: 'array' });
    const sheet = wb.Sheets[wb.SheetNames[0] ?? ''];
    if (!sheet) {
      toast.error('Fichier vide');
      return;
    }

    // Sprint 6.9.1.1 — lecture raw 2D pour gérer les lignes décoratives en début de fichier
    // (cas KOST ACADEMY : ligne 1 = titre fusionné, ligne 2 = vrais headers).
    // Test manuel : un fichier avec 'KOST ACADEMY...' en ligne 1 doit détecter 1 (= ligne 2).
    const rawMatrix = xlsx.utils.sheet_to_json<unknown[]>(sheet, {
      header: 1,
      defval: '',
      raw: false,
    });
    if (rawMatrix.length === 0) {
      toast.error('Aucune ligne détectée');
      return;
    }

    let headerIdx = 0;
    if (requiredHeaders && requiredHeaders.length > 0) {
      headerIdx = detectHeaderRow(rawMatrix, requiredHeaders);
      if (headerIdx === -1) {
        toast.error(
          "Format de fichier incorrect : aucune ligne d'en-tête reconnue. Téléchargez le template depuis le bouton ci-dessus.",
        );
        return;
      }
    }
    setDetectedHeaderRow(headerIdx);

    const headerRow = (rawMatrix[headerIdx] ?? []).map((c) => String(c ?? '').trim());
    // Dédoublonne les headers vides (`''`) pour éviter collisions de clés (ex: __EMPTY).
    const seen = new Map<string, number>();
    const uniqueHeaders = headerRow.map((h, idx) => {
      const base = h || `Colonne ${idx + 1}`;
      const cnt = seen.get(base) ?? 0;
      seen.set(base, cnt + 1);
      return cnt === 0 ? base : `${base} (${cnt + 1})`;
    });
    const dataRows = rawMatrix
      .slice(headerIdx + 1)
      .filter((r) => Array.isArray(r) && r.some((c) => String(c ?? '').trim() !== ''));
    if (dataRows.length === 0) {
      toast.error('Aucune ligne de données après les en-têtes');
      return;
    }
    const stringRows = dataRows.map((r) => {
      const obj: Record<string, string> = {};
      for (let i = 0; i < uniqueHeaders.length; i++) {
        const h = uniqueHeaders[i];
        if (h) obj[h] = String(r[i] ?? '').trim();
      }
      return obj;
    });
    setHeaders(uniqueHeaders);
    setRows(stringRows);
    setFileName(file.name);

    // Auto-mapping basé sur key OR label
    const auto: Record<string, string> = {};
    for (const f of fields) {
      const found = uniqueHeaders.find(
        (h) =>
          h.trim().toLowerCase() === f.key.toLowerCase() ||
          h.trim().toLowerCase() === f.label.toLowerCase(),
      );
      if (found) auto[f.key] = found;
    }
    setMapping(auto);
  };

  const previewRows = useMemo(() => rows.slice(0, 10), [rows]);
  const ready = fields.filter((f) => f.required).every((f) => mapping[f.key]);

  const launch = async (): Promise<void> => {
    if (!ready || rows.length === 0) return;
    setImporting(true);
    try {
      const projected = rows.map((row) => {
        const obj: Record<string, unknown> = {};
        for (const f of fields) {
          const sourceCol = mapping[f.key];
          if (!sourceCol) continue;
          const raw = row[sourceCol] ?? '';
          obj[f.key] = f.transform ? f.transform(raw) : raw === '' ? null : raw;
        }
        return obj as T;
      });
      const res = await onImport(projected);
      setResult(res);
      if (res.errors.length === 0) {
        toast.success(`${res.success} ${t('import.successCount')}`);
      } else {
        toast.warning(`${res.success} OK · ${res.errors.length} ${t('import.errors')}`);
      }
    } catch (err) {
      toast.error('Erreur : ' + (err as Error).message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
    >
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{title ?? t('import.title')}</DialogTitle>
          <DialogDescription>{t('import.drop')}</DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-5">
          {!fileName ? (
            <>
              {generateTemplate && (
                <div className="flex items-center justify-between rounded-md border border-primary/40 bg-primary/5 p-3 text-sm">
                  <div>
                    <div className="font-semibold">📋 Étape 1 — Télécharger le template</div>
                    <div className="text-xs text-muted-foreground">
                      Modèle XLSX avec les colonnes attendues + une ligne d&apos;exemple.
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => void downloadTemplate()}>
                    <Download className="h-3.5 w-3.5" /> Template
                  </Button>
                </div>
              )}
              <label className="flex h-44 cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-border bg-muted/20 transition-colors hover:bg-muted/40">
                <Upload className="h-7 w-7 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Glissez-déposez ou cliquez pour choisir un fichier
                </span>
                <span className="text-xs text-muted-foreground">XLSX · XLS · CSV · TSV</span>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv,.tsv"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void onFile(f);
                    e.target.value = '';
                  }}
                />
              </label>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 rounded-md border border-border bg-card p-2 text-sm">
                <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
                <span className="flex-1 truncate">{fileName}</span>
                <span className="text-xs text-muted-foreground">{rows.length} ligne(s)</span>
                <Button variant="ghost" size="icon" onClick={reset} aria-label="Retirer">
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {detectedHeaderRow !== null && detectedHeaderRow > 0 && (
                <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-xs text-emerald-800">
                  <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <div>
                    <div className="font-semibold">
                      Ligne d&apos;en-tête détectée : ligne {detectedHeaderRow + 1}
                    </div>
                    <div className="mt-0.5 text-[11px] text-emerald-700">
                      Les {detectedHeaderRow} ligne(s) précédente(s) (titre, sous-titre, etc.) ont
                      été ignorées automatiquement.
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <h4 className="text-sm font-semibold">{t('import.mapping')}</h4>
                <div className="grid gap-3 sm:grid-cols-2">
                  {fields.map((f) => (
                    <div key={f.key} className="space-y-1">
                      <Label>
                        {f.label}
                        {f.required && <span className="text-destructive"> *</span>}
                      </Label>
                      <select
                        value={mapping[f.key] ?? ''}
                        onChange={(e) =>
                          setMapping((prev) => ({ ...prev, [f.key]: e.target.value }))
                        }
                        className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                      >
                        <option value="">— Ignorer —</option>
                        {headers.map((h) => (
                          <option key={h} value={h}>
                            {h}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
                {!ready && <p className="text-xs text-warning">{t('import.noMapping')}</p>}
              </div>

              <div className="space-y-2">
                <h4 className="text-sm font-semibold">{t('import.preview')}</h4>
                <div className="scrollbar-thin overflow-x-auto rounded-md border border-border">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40">
                      <tr>
                        {headers.map((h) => (
                          <th
                            key={h}
                            className="whitespace-nowrap px-2 py-1.5 text-left font-medium"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((row, i) => (
                        <tr key={i} className="border-t border-border">
                          {headers.map((h) => (
                            <td
                              key={h}
                              className="whitespace-nowrap px-2 py-1 text-muted-foreground"
                            >
                              {row[h]}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {result && (
                <div className="space-y-1 rounded-md border border-border bg-muted/30 p-3 text-xs">
                  <div className="font-semibold text-success">
                    {result.success} {t('import.successCount')}
                  </div>
                  {result.errors.length > 0 && (
                    <div>
                      <div className="font-semibold text-destructive">
                        {result.errors.length} {t('import.errors')}
                      </div>
                      <ul className="mt-1 max-h-32 space-y-0.5 overflow-y-auto pl-4 text-destructive">
                        {result.errors.slice(0, 30).map((e, i) => (
                          <li key={i} className="list-disc">
                            {e}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={importing}>
            {t('common.cancel')}
          </Button>
          <Button disabled={!fileName || !ready || importing} onClick={() => void launch()}>
            {importing && <Loader2 className="h-4 w-4 animate-spin" />}
            {importing ? t('import.importing') : t('import.import')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
